import React, { useMemo, useState } from 'react';
import { DownloadIcon, ColumnsIcon, SearchIcon } from 'lucide-react';
import Card from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import DataTable from '../../../components/ui/DataTable';
import EmptyState from '../../../components/ui/EmptyState';
import { TeachingIntention } from '../../../types/intentions';
import {
  exportIntentionsFull,
  rankedPreferenceTexts,
  employmentTypeText,
  ftePreferenceText,
  dayOffPreferenceText,
  yearLevelsText,
  subjectsText,
  ruahSubjectsText,
  leaveText,
  tilActivitiesText,
  year9StretchText,
  notReturningReasonText,
  leaveNotesText,
  comfortableAreasText,
} from '../../../utils/exportIntentions';
import { useToast } from '../../../contexts/ToastContext';
import { displayLearningArea } from '../../../constants/learningAreas';

interface IntentionsSubmissionsTableProps {
  /** The dataset for the tab — already scoped to the selected year (and the
   *  page-level department filter). All statuses are included. */
  intentions: TeachingIntention[];
}

interface ColumnDef {
  key: string;
  header: string;
  defaultVisible: boolean;
  sortable: boolean;
  cell: (i: TeachingIntention) => React.ReactNode;
  /** Right-align + monospace tabular figures (codes, IDs, FTE, minutes, dates). */
  numeric?: boolean;
}

// Format a Date | string as a short local date.
function fmtDate(value: Date | string | null | undefined): string {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString();
}

// Truncated text with a full-text tooltip for long free-text fields.
function truncated(text: string | null | undefined, max = 60): React.ReactNode {
  if (!text) return <span className="text-ink-400">—</span>;
  const display = text.length > max ? `${text.slice(0, max)}…` : text;
  return <span title={text}>{display}</span>;
}

const STATUS_BADGE: Record<string, string> = {
  draft: 'bg-ink-100 text-ink-900',
  submitted: 'bg-navy-100 text-navy-800',
  approved: 'bg-ok-bg text-ok-fg',
  hr_approved: 'bg-ok-bg text-ok-fg',
  hr_flagged: 'bg-danger-bg text-danger-fg',
  imported: 'bg-navy-100 text-navy-800',
};

function statusBadge(status: string): React.ReactNode {
  const cls = STATUS_BADGE[status] ?? 'bg-ink-100 text-ink-900';
  return (
    <span className={`px-2 py-1 inline-flex text-meta leading-5 font-semibold rounded-full ${cls}`}>
      {status}
    </span>
  );
}

function yesPill(value: boolean | undefined): React.ReactNode {
  return value
    ? <span className="px-2 py-1 inline-flex text-meta leading-5 font-semibold rounded-full bg-warn-bg text-warn-fg">Yes</span>
    : <span className="text-ink-400">—</span>;
}

// All available columns. `defaultVisible` controls the initial picker state.
// The default set surfaces what teachers actually answer on the intentions form;
// the rest (admin/HR metadata, imported-only legacy fields) are toggle-on.
const COLUMN_DEFS: ColumnDef[] = [
  // --- Identity ---
  { key: 'staffName', header: 'Staff Name', defaultVisible: true, sortable: true, cell: i => i.staffName },
  { key: 'staffId', header: 'Staff ID', defaultVisible: true, sortable: true, numeric: true, cell: i => i.staffId },
  { key: 'department', header: 'Learning Area', defaultVisible: true, sortable: true, cell: i => displayLearningArea(i.department) },
  { key: 'status', header: 'Status', defaultVisible: true, sortable: true, cell: i => statusBadge(i.status) },
  // --- What they submitted on the form ---
  { key: 'intentionToReturn', header: 'Return Intent', defaultVisible: true, sortable: true, cell: i => i.intentionToReturn },
  { key: 'employmentType', header: 'Employment Type', defaultVisible: true, sortable: false, cell: i => truncated(employmentTypeText(i)) },
  { key: 'ftePreference', header: 'FTE Preference', defaultVisible: true, sortable: false, cell: i => truncated(ftePreferenceText(i)) },
  { key: 'dayOffPreference', header: 'Day-off Preference', defaultVisible: true, sortable: false, cell: i => truncated(dayOffPreferenceText(i)) },
  { key: 'targetFTE', header: 'Target FTE', defaultVisible: true, sortable: true, numeric: true, cell: i => i.targetFTE?.toFixed?.(1) ?? i.targetFTE },
  { key: 'teachingPreferences', header: 'Learning Areas', defaultVisible: true, sortable: false, cell: i => truncated(rankedPreferenceTexts(i).join('; ')) },
  { key: 'yearLevels', header: 'Year Levels', defaultVisible: true, sortable: false, cell: i => truncated(yearLevelsText(i)) },
  { key: 'submittedAt', header: 'Submitted', defaultVisible: true, sortable: true, numeric: true, cell: i => fmtDate(i.submittedAt) },
  { key: 'isLate', header: 'Late?', defaultVisible: true, sortable: true, cell: i => yesPill(i.isLate) },
  // --- More form detail (toggle on) ---
  { key: 'subjects', header: 'Specific Subjects', defaultVisible: false, sortable: false, cell: i => truncated(subjectsText(i)) },
  { key: 'ruahSubjects', header: 'RUAH Subjects', defaultVisible: false, sortable: false, cell: i => truncated(ruahSubjectsText(i)) },
  { key: 'comfortableAreas', header: 'Comfortable Areas', defaultVisible: false, sortable: false, cell: i => truncated(comfortableAreasText(i)) },
  { key: 'year9Stretch', header: 'Year 9 Stretch', defaultVisible: false, sortable: false, cell: i => truncated(year9StretchText(i)) },
  { key: 'tilActivities', header: 'TIL Activities', defaultVisible: false, sortable: false, cell: i => truncated(tilActivitiesText(i)) },
  { key: 'leave', header: 'Leave Plans', defaultVisible: false, sortable: false, cell: i => truncated(leaveText(i)) },
  { key: 'leaveNotes', header: 'Leave Notes', defaultVisible: false, sortable: false, cell: i => truncated(leaveNotesText(i)) },
  { key: 'notReturningReason', header: 'Not Returning Reason', defaultVisible: false, sortable: false, cell: i => truncated(notReturningReasonText(i)) },
  { key: 'additionalComments', header: 'Additional Comments', defaultVisible: false, sortable: false, cell: i => truncated(i.additionalComments) },
  // --- Allowances / leadership ---
  { key: 'polPosition', header: 'POL Position', defaultVisible: false, sortable: true, cell: i => truncated(i.polPosition) },
  { key: 'polMinutes', header: 'POL Minutes', defaultVisible: false, sortable: true, numeric: true, cell: i => i.polMinutes ?? <span className="text-ink-400">—</span> },
  { key: 'otherAllowances', header: 'Other Allowances', defaultVisible: false, sortable: false, cell: i => truncated(i.otherAllowances) },
  { key: 'leadingLearningRole', header: 'Leading Learning Role', defaultVisible: false, sortable: false, cell: i => truncated(i.leadingLearningRole) },
  { key: 'additionalResponsibilities', header: 'Additional Responsibilities', defaultVisible: false, sortable: false, cell: i => truncated(i.additionalResponsibilities) },
  // --- Imported-only / legacy ---
  { key: 'preferredDays', header: 'Preferred Days', defaultVisible: false, sortable: false, cell: i => truncated(i.preferredDays) },
  { key: 'secondaryDepartments', header: 'Secondary Learning Areas', defaultVisible: false, sortable: false, cell: i => truncated((i.secondaryDepartments ?? []).join(', ')) },
  { key: 'email', header: 'Email', defaultVisible: false, sortable: true, cell: i => truncated(i.email) },
  // --- HR / admin metadata ---
  { key: 'hrDiscussFlag', header: 'HR Discuss?', defaultVisible: false, sortable: true, cell: i => yesPill(i.hrDiscussFlag) },
  { key: 'hrReviewedBy', header: 'HR Reviewed By', defaultVisible: false, sortable: true, cell: i => truncated(i.hrReviewedBy) },
  { key: 'hrReviewedAt', header: 'HR Reviewed At', defaultVisible: false, sortable: true, numeric: true, cell: i => fmtDate(i.hrReviewedAt) },
  { key: 'hrNotes', header: 'HR Notes', defaultVisible: false, sortable: false, cell: i => truncated(i.hrNotes) },
  { key: 'enteredByStaffId', header: 'Entered By', defaultVisible: false, sortable: true, numeric: true, cell: i => truncated(i.enteredByStaffId) },
  { key: 'importSource', header: 'Import Source', defaultVisible: false, sortable: true, cell: i => truncated(i.importSource) },
  { key: 'lastUpdatedAt', header: 'Last Updated', defaultVisible: false, sortable: true, numeric: true, cell: i => fmtDate(i.lastUpdatedAt) },
];

const STATUS_OPTIONS = ['all', 'draft', 'submitted', 'imported', 'hr_approved', 'hr_flagged', 'approved'];

const IntentionsSubmissionsTable: React.FC<IntentionsSubmissionsTableProps> = ({ intentions }) => {
  const { addToast } = useToast();
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [search, setSearch] = useState<string>('');
  const [showColumnPicker, setShowColumnPicker] = useState<boolean>(false);
  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(
    () => new Set(COLUMN_DEFS.filter(c => c.defaultVisible).map(c => c.key))
  );
  const [exporting, setExporting] = useState<boolean>(false);

  // Rows shown in the grid: apply the tab's own status filter + free-text search.
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return intentions.filter(i => {
      if (statusFilter !== 'all' && i.status !== statusFilter) return false;
      if (!q) return true;
      return (
        (i.staffName ?? '').toLowerCase().includes(q) ||
        (i.staffId ?? '').toLowerCase().includes(q) ||
        (i.department ?? '').toLowerCase().includes(q)
      );
    });
  }, [intentions, statusFilter, search]);

  // DataTable columns derived from the picker selection.
  const columns = useMemo(
    () =>
      COLUMN_DEFS.filter(c => visibleKeys.has(c.key)).map(c => ({
        key: c.key,
        header: c.header,
        cell: c.cell,
        sortable: c.sortable,
        numeric: c.numeric,
      })),
    [visibleKeys]
  );

  const toggleColumn = (key: string) => {
    setVisibleKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Export EVERY field of EVERY row in the provided dataset (ignores the tab's
  // status filter, search, and column visibility) — a complete data dump.
  const handleExportAll = async () => {
    try {
      setExporting(true);
      await new Promise(resolve => setTimeout(resolve, 50));
      exportIntentionsFull(intentions, 'excel');
      addToast(`Exported all ${intentions.length} intention records to Excel`, 'success');
    } catch (err) {
      console.error('Full export failed:', err);
      addToast('Export failed. Please try again.', 'error');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Heading — labels the tab and makes the export scope explicit. */}
      <div>
        <h3 className="text-card-title leading-6 text-ink-900">
          All submissions
        </h3>
        <p className="mt-1 text-body-sm text-ink-500">
          Every intention on file for the selected year, in full detail. Showing{' '}
          <span className="font-mono tabular-nums">{rows.length}</span> of{' '}
          <span className="font-mono tabular-nums">{intentions.length}</span>. Use the column
          picker to add fields; "Export all data" downloads every record and column, ignoring
          the status filter and search below.
        </p>
      </div>
      {/* Toolbar */}
      <Card>
        <div className="p-4 flex flex-wrap items-end gap-4">
          <div>
            <label htmlFor="submission-status" className="block text-body-sm font-medium text-ink-700 mb-1">
              Status
            </label>
            <select
              id="submission-status"
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="mt-1 block pl-3 pr-10 py-2 text-base border-ink-300 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm rounded-md"
            >
              {STATUS_OPTIONS.map(opt => (
                <option key={opt} value={opt}>
                  {opt === 'all' ? 'All statuses' : opt}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <label htmlFor="submission-search" className="block text-body-sm font-medium text-ink-700 mb-1">
              Search
            </label>
            <input
              id="submission-search"
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Name, staff ID, or learning area…"
              className="mt-1 block w-full pl-3 pr-3 py-2 text-base border-ink-300 focus:outline-none focus:ring-navy-500 focus:border-navy-500 sm:text-sm rounded-md"
            />
          </div>
          <div className="relative">
            <Button
              variant="secondary"
              leftIcon={<ColumnsIcon className="h-4 w-4" />}
              onClick={() => setShowColumnPicker(v => !v)}
            >
              Columns
            </Button>
            {showColumnPicker && (
              <div className="absolute right-0 z-20 mt-2 w-64 max-h-80 overflow-y-auto bg-white border border-ink-200 rounded-md shadow-lg p-3">
                {COLUMN_DEFS.map(c => (
                  <label key={c.key} className="flex items-center space-x-2 py-1 text-body-sm text-ink-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={visibleKeys.has(c.key)}
                      onChange={() => toggleColumn(c.key)}
                      className="rounded border-ink-300 text-navy-600 focus:ring-navy-500"
                    />
                    <span>{c.header}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
          <div>
            <Button
              variant="primary"
              leftIcon={<DownloadIcon className="h-4 w-4" />}
              onClick={handleExportAll}
              disabled={exporting || intentions.length === 0}
            >
              {exporting ? 'Exporting…' : 'Export all data'}
            </Button>
          </div>
        </div>
      </Card>

      {/* Grid (built-in DataTable export suppressed by omitting title/actions) */}
      <DataTable
        data={rows}
        columns={columns}
        keyField="id"
        pagination={true}
        pageSize={25}
        emptyState={
          <EmptyState
            icon={<SearchIcon className="h-6 w-6" />}
            title="No intentions match the current filters"
            description="Try changing the status filter or search above."
          />
        }
      />
    </div>
  );
};

export default IntentionsSubmissionsTable;
